import React, { useState, useEffect, useRef } from 'react';
import { categoryMeta } from '@/lib/categories';
import { personalityMeta } from '@/lib/personalities';
import { ITINERARY } from '@/constants/testIds';
import { Clock, Users, MapPin, Pencil, ArrowRightLeft, GripVertical, StickyNote, ChevronDown, Loader2 } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

function buildMapsUrl(activity, tripDestination) {
    const parts = [activity?.location_name];
    if (activity?.neighborhood) {
        parts.push(activity.neighborhood);
    } else if (tripDestination) {
        parts.push(tripDestination);
    }
    const query = parts.filter(Boolean).join(', ');
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

function TimeEditor({ value, onSave, dataTestid }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value || '09:00');
    const inputRef = useRef(null);
    useEffect(() => { setDraft(value || '09:00'); }, [value]);
    useEffect(() => {
        if (editing && inputRef.current) inputRef.current.focus();
    }, [editing]);

    const commit = () => {
        setEditing(false);
        const v = (draft || '').trim();
        if (v && v !== value) onSave(v);
    };

    if (!editing) {
        return (
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setEditing(true); }}
                data-testid={dataTestid}
                className="font-display text-lg font-semibold text-[var(--ns-text)] leading-none hover:text-[var(--ns-clay)] inline-flex items-center gap-1 group/time"
                title="Tap to edit start time"
            >
                {value || '—'}
                <Pencil size={10} className="opacity-0 group-hover/time:opacity-60 transition" />
            </button>
        );
    }
    return (
        <input
            ref={inputRef}
            type="time"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); commit(); }
                if (e.key === 'Escape') { setDraft(value); setEditing(false); }
            }}
            data-testid={`${dataTestid}-input`}
            className="font-display text-base font-semibold text-[var(--ns-text)] border border-[var(--ns-border)] rounded-md px-1 py-0.5 w-[88px] focus:outline-none focus:border-[var(--ns-clay)]"
        />
    );
}

function DurationEditor({ value, onSave, dataTestid }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value || 60);
    const inputRef = useRef(null);
    useEffect(() => { setDraft(value || 60); }, [value]);
    useEffect(() => {
        if (editing && inputRef.current) { inputRef.current.focus(); inputRef.current.select(); }
    }, [editing]);

    const commit = () => {
        setEditing(false);
        const n = parseInt(draft, 10);
        if (!Number.isNaN(n) && n !== value && n >= 5 && n <= 1440) onSave(n);
    };

    if (!editing) {
        return (
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setEditing(true); }}
                data-testid={dataTestid}
                className="text-[10px] uppercase tracking-widest text-[var(--ns-text3)] mt-1 inline-flex items-center gap-1 hover:text-[var(--ns-clay)]"
                title="Tap to edit duration"
            >
                <Clock size={10} /> {value}m
            </button>
        );
    }
    return (
        <div className="mt-1 inline-flex items-center gap-1 text-[var(--ns-text2)]">
            <Clock size={10} />
            <input
                ref={inputRef}
                type="number"
                min="5"
                max="1440"
                step="5"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={commit}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); commit(); }
                    if (e.key === 'Escape') { setDraft(value); setEditing(false); }
                }}
                data-testid={`${dataTestid}-input`}
                className="text-xs w-[56px] border border-[var(--ns-border)] rounded-md px-1 py-0.5 focus:outline-none focus:border-[var(--ns-clay)] tabular-nums"
            />
            <span className="text-[10px] uppercase tracking-widest">min</span>
        </div>
    );
}

export default function ActivityCard({ activity, onUpdateField, onMoveToDay, onEditActivity, onSaveNote, availableDays = [], currentDayNumber, tripDestination, dragControls, isDragging }) {
    const cat = categoryMeta(activity.category);
    const Icon = cat.icon;
    const mapsUrl = buildMapsUrl(activity, tripDestination);
    const otherDays = (availableDays || []).filter((d) => d?.day_number !== currentDayNumber);
    const hasNote = !!(activity.note && activity.note.trim());
    const [noteOpen, setNoteOpen] = useState(false);
    const [noteDraft, setNoteDraft] = useState(activity.note || '');
    const [savingNote, setSavingNote] = useState(false);
    useEffect(() => { setNoteDraft(activity.note || ''); }, [activity.note]);

    const commitNote = async () => {
        const next = (noteDraft || '').trim().slice(0, 300);
        if (next === (activity.note || '').trim()) { setNoteOpen(false); return; }
        if (!onSaveNote) { setNoteOpen(false); return; }
        setSavingNote(true);
        try {
            await onSaveNote(next);
            setNoteOpen(false);
        } finally {
            setSavingNote(false);
        }
    };

    return (
        <div
            data-testid={ITINERARY.activityCard}
            className={`ns-card ns-card-hover p-5 sm:p-6 ${isDragging ? 'shadow-xl ring-2 ring-[var(--ns-clay)] cursor-grabbing' : ''}`}
        >
            <div className="flex items-start gap-3 sm:gap-4">
                {/* Drag handle */}
                {dragControls && (
                    <button
                        type="button"
                        data-testid={ITINERARY.activityDragHandle}
                        onPointerDown={(e) => { e.preventDefault(); dragControls.start(e); }}
                        onClick={(e) => e.stopPropagation()}
                        title="Drag to reorder"
                        aria-label="Drag to reorder activity"
                        className="touch-none flex-shrink-0 w-7 h-12 rounded-lg flex items-center justify-center text-[var(--ns-text3)] hover:text-[var(--ns-text)] hover:bg-[var(--ns-soft)] cursor-grab active:cursor-grabbing transition self-center"
                    >
                        <GripVertical size={16} />
                    </button>
                )}
                {/* Time gutter */}
                <div className="flex flex-col items-center min-w-[80px] pt-1">
                    <TimeEditor
                        value={activity.time_slot}
                        onSave={(v) => onUpdateField?.('time_slot', v)}
                        dataTestid={ITINERARY.activityTime}
                    />
                    <DurationEditor
                        value={activity.duration_minutes}
                        onSave={(v) => onUpdateField?.('duration_minutes', v)}
                        dataTestid={ITINERARY.activityDuration}
                    />
                </div>
                <div className="w-px self-stretch" style={{ background: 'var(--ns-border)' }} />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span
                                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-medium px-2 py-1 rounded-full"
                                style={{ background: 'var(--ns-soft)', color: cat.color }}
                            >
                                <Icon size={11} /> {cat.label}
                            </span>
                            {hasNote && (
                                <button
                                    type="button"
                                    onClick={() => setNoteOpen((v) => !v)}
                                    data-testid={ITINERARY.activityNoteBadge}
                                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-medium px-2 py-1 rounded-full border border-[var(--ns-clay)] text-[var(--ns-clay)] hover:bg-[var(--ns-clay)] hover:text-white transition"
                                    title="This activity has a group note"
                                >
                                    <StickyNote size={11} /> Note
                                </button>
                            )}
                        </div>
                        <div className="flex items-center gap-1.5">
                            {onEditActivity && (
                                <button
                                    type="button"
                                    onClick={onEditActivity}
                                    data-testid={ITINERARY.activityEditButton}
                                    className="h-7 w-7 rounded-full border border-[var(--ns-border)] flex items-center justify-center text-[var(--ns-text2)] hover:text-[var(--ns-clay)] hover:border-[var(--ns-clay)] transition"
                                    title="Edit activity"
                                    aria-label="Edit activity"
                                >
                                    <Pencil size={12} />
                                </button>
                            )}
                            {otherDays.length > 0 && onMoveToDay && (
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <button
                                            type="button"
                                            data-testid={ITINERARY.activityMoveToTrigger}
                                            className="h-7 px-2.5 rounded-full border border-[var(--ns-border)] inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[var(--ns-text2)] hover:text-[var(--ns-text)] hover:border-[var(--ns-clay)] transition"
                                            title="Move to another day"
                                        >
                                            <ArrowRightLeft size={11} />
                                            <span className="hidden sm:inline">Move</span>
                                        </button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="rounded-2xl min-w-[160px]">
                                        <DropdownMenuLabel className="text-xs text-[var(--ns-text3)] font-normal">Move to…</DropdownMenuLabel>
                                        <DropdownMenuSeparator />
                                        {otherDays.map((d) => (
                                            <DropdownMenuItem
                                                key={d.day_number}
                                                onSelect={() => onMoveToDay(d.day_number)}
                                                data-testid={`${ITINERARY.activityMoveToOption}-${d.day_number}`}
                                                className="cursor-pointer text-sm"
                                            >
                                                Day {d.day_number}
                                            </DropdownMenuItem>
                                        ))}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            )}
                        </div>
                    </div>

                    <div className="mt-2 flex items-start gap-2">
                        <h4 className="font-display text-lg sm:text-xl font-semibold tracking-tight text-[var(--ns-text)] leading-snug flex-1 min-w-0">
                            {activity.location_name}
                        </h4>
                        <a
                            href={mapsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            data-testid={ITINERARY.activityMapsLink}
                            title="Open in Google Maps"
                            aria-label={`Open ${activity.location_name} in Google Maps`}
                            className="flex-shrink-0 w-8 h-8 rounded-full border border-[var(--ns-border)] flex items-center justify-center text-[var(--ns-text2)] hover:text-[var(--ns-clay)] hover:border-[var(--ns-clay)] transition"
                        >
                            <MapPin size={14} />
                        </a>
                    </div>

                    {activity.neighborhood && (
                        <div className="text-xs text-[var(--ns-text3)] mt-0.5">{activity.neighborhood}</div>
                    )}

                    {activity.description && (
                        <p className="text-sm text-[var(--ns-text2)] mt-2 leading-relaxed">{activity.description}</p>
                    )}

                    {/* Personality badges */}
                    {activity.personality_types_served?.length > 0 && (
                        <div className="flex items-center gap-2 mt-3 flex-wrap">
                            <span className="text-[10px] uppercase tracking-widest text-[var(--ns-text3)]">Serves</span>
                            {activity.personality_types_served.map((p) => {
                                const meta = personalityMeta(p);
                                return (
                                    <span
                                        key={p}
                                        className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full"
                                        style={{ background: meta.tint, color: meta.color }}
                                    >
                                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: meta.color }} />
                                        {p.replace('The ', '')}
                                    </span>
                                );
                            })}
                        </div>
                    )}

                    {activity.voted_must_have_names?.length > 0 && (
                        <div className="text-xs text-[var(--ns-text3)] mt-3 inline-flex items-center gap-1.5">
                            <Users size={11} />
                            Must-have for {activity.voted_must_have_names.join(', ')}
                        </div>
                    )}

                    {/* Collapsible group note */}
                    {onSaveNote && (
                        <div className="mt-4 pt-3 border-t border-dashed border-[var(--ns-border)]">
                            {!noteOpen ? (
                                <button
                                    type="button"
                                    onClick={() => setNoteOpen(true)}
                                    data-testid={ITINERARY.activityNoteToggle}
                                    className="w-full text-left inline-flex items-center gap-2 text-xs text-[var(--ns-text2)] hover:text-[var(--ns-clay)] transition"
                                >
                                    <StickyNote size={12} className="text-[var(--ns-text3)]" />
                                    {hasNote ? (
                                        <span className="flex-1 truncate italic">{activity.note}</span>
                                    ) : (
                                        <span className="uppercase tracking-widest text-[10px] text-[var(--ns-text3)]">+ Add note</span>
                                    )}
                                    <ChevronDown size={12} className="text-[var(--ns-text3)]" />
                                </button>
                            ) : (
                                <div>
                                    <label className="text-[10px] uppercase tracking-[0.18em] text-[var(--ns-text3)] flex items-center justify-between mb-1.5">
                                        <span>Group note <span className="normal-case tracking-normal text-[var(--ns-text3)]">· visible to everyone</span></span>
                                        <span className="tabular-nums normal-case tracking-normal">{(noteDraft || '').length}/300</span>
                                    </label>
                                    <Textarea
                                        value={noteDraft}
                                        onChange={(e) => setNoteDraft(e.target.value.slice(0, 300))}
                                        rows={2}
                                        maxLength={300}
                                        placeholder="e.g. Reservation under Nadia, arrive 10 min early."
                                        data-testid={ITINERARY.activityNoteTextarea}
                                        className="rounded-xl border-[var(--ns-border)] bg-white text-sm resize-none"
                                    />
                                    <div className="flex items-center justify-end gap-2 mt-2">
                                        <Button
                                            variant="ghost"
                                            onClick={() => { setNoteDraft(activity.note || ''); setNoteOpen(false); }}
                                            disabled={savingNote}
                                            data-testid={ITINERARY.activityNoteCancel}
                                            className="h-8 px-3 text-xs text-[var(--ns-text2)]"
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            onClick={commitNote}
                                            disabled={savingNote}
                                            data-testid={ITINERARY.activityNoteSave}
                                            className="h-8 px-3 text-xs text-white rounded-full"
                                            style={{ background: 'var(--ns-navy)' }}
                                        >
                                            {savingNote && <Loader2 size={12} className="mr-1.5 animate-spin" />}
                                            Save note
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
